import { cn } from '@/lib/utils';

/**
 * Quieter channel tag for the redesigned Loan Center. The current
 * ProspectTable uses loud solid fills (bg-green-500 / bg-blue-500 / etc.) that
 * dominate the row; the redesign explicitly tones them down to muted tinted
 * pills so the row is scannable.
 */
interface ChannelTagProps {
  channel?: string | null;
  className?: string;
}

const CHANNEL_STYLE: Record<string, { bg: string; text: string; dot: string }> = {
  'Banked - Retail': { bg: 'hsl(142 50% 94%)', text: 'hsl(142 71% 25%)', dot: 'hsl(142 71% 38%)' },
  Brokered: { bg: 'hsl(213 90% 95%)', text: 'hsl(213 80% 30%)', dot: 'hsl(213 80% 45%)' },
  'Banked - Wholesale': { bg: 'hsl(173 50% 94%)', text: 'hsl(173 60% 25%)', dot: 'hsl(173 60% 38%)' },
  Correspondent: { bg: 'hsl(27 70% 94%)', text: 'hsl(27 70% 30%)', dot: 'hsl(27 70% 45%)' }
};

const LABEL_OVERRIDES: Record<string, string> = {
  'Banked - Retail': 'Retail',
  'Banked - Wholesale': 'Wholesale'
};

export function ChannelTag({ channel, className }: ChannelTagProps) {
  const key = channel && CHANNEL_STYLE[channel] ? channel : null;
  const style = key ? CHANNEL_STYLE[key] : null;
  const label = channel ? (LABEL_OVERRIDES[channel] ?? channel) : 'Unknown';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
        !style && 'bg-muted text-muted-foreground',
        className
      )}
      style={style ? { backgroundColor: style.bg, color: style.text } : undefined}
    >
      {style && (
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: style.dot }}
        />
      )}
      {label}
    </span>
  );
}
